import type { ToolCallMessagePartProps } from '@assistant-ui/react';
import { createContext } from 'react';

import type { MantineColor } from '#core/lib/formatters/colors.ts';

import type { AnyToolLabelValues, StyledIcon } from '#plugin-chat/lib/tool/message-formatters.ts';

/**
 * Per-part chrome scope provided once by `ToolDisplay` at the chrome root.
 * Everything a primitive in the chrome subtree could legitimately read —
 * the tool identity and its declared presentation defaults — lives here;
 * primitives consume it with `use()` instead of taking props for it.
 */
export type ToolChromeScope = {
  toolName: string | undefined;
  /** Tool color (`<Tool color>`): derives the default complete-row color. */
  color?: MantineColor;
  /** Tool icon (`<Tool icon>`): default complete-row icon. */
  icon?: StyledIcon;
  /** i18n interpolation values for the conventional status labels. */
  labelValues?: AnyToolLabelValues;
  /**
   * Part-supplied interrupt plumbing. The awaitingInput renderer receives
   * these directly; the scope copy exists for components that must show
   * interrupt UI outside the awaitingInput kind (e.g. the upstream
   * first-interrupt race where the part status never flips).
   */
  resume?: ToolCallMessagePartProps<Record<string, unknown>, unknown>['resume'];
  interrupt?: ToolCallMessagePartProps<Record<string, unknown>, unknown>['interrupt'];
  /** Portal target rendered after the chrome grid — `ToolAside`'s destination. */
  asideElement: HTMLElement | null;
};

export const ToolChromeContext = createContext<ToolChromeScope>({
  toolName: undefined,
  asideElement: null,
});
